import type { ReactNode } from "react";
import { BackLink } from "../../components/ui/BackLink";

export const LSTAGES = ["Who", "Learn", "Confirm", "First goal"] as const;
export type LearnStage = (typeof LSTAGES)[number];

interface Props {
  /** Where the flow is; none when changing a confirmed profile, which has no steps. */
  stage?: LearnStage;
  title: string;
  sub: ReactNode;
  crumb?: string;
  back?: { to: string; label: string };
  /** What sits at the right of the back row when the flow has no steps: the profile's versions. */
  aside?: ReactNode;
}

/**
 * Where the add-a-product flow stands on a phone or an iPad, in words beside four short bars:
 * "Step 3 of 4: Confirm". `word` is left out where the title already says it.
 */
export function StepLine({ at, word }: { at: number; word?: string }) {
  return (
    <span className="pd-step">
      <span className="pd-bars" aria-hidden="true">
        {LSTAGES.map((l, k) => (
          <i key={l} className={k < at ? "on" : undefined} />
        ))}
      </span>
      <span>
        Step {at} of {LSTAGES.length}
        {word ? `: ${word}` : ""}
      </span>
    </span>
  );
}

/** The add-a-product head (the prototype's `.flowhead`): back, the flow's name, where it is, then the title. */
export function FlowHead({ stage, title, sub, crumb = "Add a product", back = { to: "/products", label: "Products" }, aside }: Props) {
  const i = stage ? LSTAGES.indexOf(stage) : -1;
  return (
    <header className="ph flowhead">
      <div className="ctx">
        <BackLink to={back.to} label={back.label} />
        <span className="crumbs fh-crumb">
          <b>{crumb}</b>
        </span>
        {stage && (
          <span className="ctx-r fprog">
            <span className="fprog-l">{LSTAGES[i]}</span>
            <ol className="stages">
              {LSTAGES.map((l, k) => (
                <li
                  key={l}
                  className={k < i ? "done" : k === i ? "on" : undefined}
                  title={l}
                  aria-label={`${l}${k < i ? ", done" : k === i ? ", current step" : ""}`}
                />
              ))}
            </ol>
          </span>
        )}
        {!stage && aside && <span className="ctx-r">{aside}</span>}
      </div>
      <div className="ph-t wz-h">
        <h1 className="ptitle">{title}</h1>
      </div>
      <p className="ph-s">{sub}</p>
    </header>
  );
}
